import {useEffect,useState} from 'react';
import {dominantColor,tintFromColor} from '../shared/artwork-color.js';
import {serverIconPath} from './artwork.js';

// Samples a station icon's dominant color in the browser. The icon is read through the server's
// same-origin `/icon/bytes` route, so cached S3 icons never taint the canvas.
const colors=new Map(),pending=new Map();
const MAX_ACTIVE=4;let running=0;const waiting=[];

async function limited(task){
  if(running>=MAX_ACTIVE)await new Promise(resolve=>waiting.push(resolve));
  running++;
  try{return await task();}finally{running--;waiting.shift()?.();}
}

const cacheKey=station=>`${station.stationuuid}:${station.favicon||''}`;

async function sample(station){
  const response=await fetch(`${serverIconPath(station)}/bytes`,{credentials:'same-origin'});
  if(!response.ok)return null;
  // Raster formats only: SVG blobs cannot be decoded by createImageBitmap and are skipped.
  const bitmap=await createImageBitmap(await response.blob());
  try{
    const side=24,canvas=typeof OffscreenCanvas==='function'?new OffscreenCanvas(side,side):Object.assign(document.createElement('canvas'),{width:side,height:side});
    const context=canvas.getContext('2d',{willReadFrequently:true});
    context.drawImage(bitmap,0,0,side,side);
    return dominantColor(context.getImageData(0,0,side,side).data);
  }finally{bitmap.close?.();}
}

export function stationColor(station){
  if(!station?.favicon||!serverIconPath(station))return Promise.resolve(null);
  const key=cacheKey(station);
  if(colors.has(key))return Promise.resolve(colors.get(key));
  if(!pending.has(key))pending.set(key,limited(()=>sample(station)).catch(()=>null).then(color=>{colors.set(key,color);pending.delete(key);return color;}));
  return pending.get(key);
}

// The station's tint, or null when disabled, not yet sampled, or the icon has no usable color.
// `ready` defers sampling until the icon is actually shown (e.g. scrolled into view).
export function useStationTint(station,{enabled=true,ready=true}={}){
  const key=station?.favicon&&serverIconPath(station)?cacheKey(station):null;
  const [sampled,setSampled]=useState(null);
  useEffect(()=>{
    if(!enabled||!ready||!key||colors.has(key))return;
    let active=true;
    stationColor(station).then(color=>{if(active)setSampled({key,color});});
    return()=>{active=false;};
  },[enabled,ready,key]);
  if(!enabled||!key)return null;
  const color=colors.has(key)?colors.get(key):sampled?.key===key?sampled.color:null;
  return color?tintFromColor(color):null;
}
